import { useState } from "react";
import useGet from "../useGet";
import useRead from "../useRead";
import useCreate from "../useCreate";
import toast from "react-hot-toast";

const useTestimonialList = () => {
  // Use useGet for GET request handling
  const { data, paramsObject, setParamsObject } = useGet({
    url: "get-testimonial-list",
    initialParams: {
      page_number: 0,
      limit: 10,
      search_query: "",
    },
  });

  const [testimonialId, setTestimonialId] = useState(null);
  const [selectedTestimonial, setSelectedTestimonial] = useState(null);

  // Status Logic
  const [testimonialStatus, setTestimonialStatus] = useState(false);
  const [isToggleLoading, setIsToggleLoading] = useState(false);
  const [previousStatus, setPreviousStatus] = useState(false);

  // Fetch testimonial status
  useRead({
    url: "get-testimonial-status",
    method: "GET",
    initialData: {},
    onSuccess: (data) => {
      setTestimonialStatus(data.testimonial_status);
      setHeader(data.header || "");
      setInitialHeader(data.header || "");
    },
    onError: (err) => {
      toast.error(err?.message || "Failed to fetch testimonial status");
    },
  });

  // Update testimonial status
  const { setDataToServer } = useCreate({
    url: "change-testimonial-status",
    refreshUrl: "get-testimonial-status",
    onSuccess: (data) => {},
    onError: (err) => {
      // Revert to previous state on error
      setTestimonialStatus(previousStatus);
      setIsToggleLoading(false);
      toast.error(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to update testimonial status",
      );
    },
  });

  const handleTestimonialStatusChange = async (event) => {
    const newStatus = event.target.checked;
    // Store the current status before changing
    setPreviousStatus(testimonialStatus);
    setIsToggleLoading(true);
    setTestimonialStatus(newStatus);
    setDataToServer.mutate(
      {
        enable: newStatus,
      },
      {
        onSuccess: () => {
          toast.success(
            `Testimonials ${newStatus ? "enabled" : "disabled"} successfully`,
          );
          setIsToggleLoading(false);
        },
        onError: (err) => {
          setTestimonialStatus(previousStatus);
          setIsToggleLoading(false);
          toast.error(
            err?.response?.data?.message ||
              err?.message ||
              "Failed to update testimonial status",
          );
        },
      },
    );
  };

  // Header Logic
  const [header, setHeader] = useState("");
  const [initialHeader, setInitialHeader] = useState("");
  const [isHeaderUpdating, setIsHeaderUpdating] = useState(false);

  // Update Header
  const { setDataToServer: updateHeader } = useCreate({
    url: "update-testimonial-header",
    refreshUrl: "get-testimonial-status",
    onSuccess: (data) => {
      toast.success("Header updated successfully");
      setInitialHeader(header); // Update initial header to new value
      setIsHeaderUpdating(false);
    },
    onError: (err) => {
      setIsHeaderUpdating(false);
      toast.error(
        err?.response?.data?.message || err?.message || "Failed to update header",
      );
    },
  });

  const handleHeaderChange = (e) => {
    setHeader(e.target.value);
  };

  const handleSaveHeader = () => {
    setIsHeaderUpdating(true);
    updateHeader.mutate({ header });
  };

  const isHeaderChanged = header !== initialHeader;

  // Modals
  const [openAddModal, setOpenAddModal] = useState(false);
  const [isOpenEditModal, setIsOpenEditModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);

  // Search
  const handleSearchVal = (e) => {
    setParamsObject((prev) => ({
      ...prev,
      search_query: e.target.value,
      page_number: 0,
    }));
  };

  // Pagination
  const handlePageClick = (data) => {
    setParamsObject((prev) => ({
      ...prev,
      page_number: data.selected,
    }));
  };

  // Modal Handlers
  const handleOpenAddModal = () => setOpenAddModal(true);

  const handleOpenEditModal = (item) => {
    setTestimonialId(item.testimonial_id);
    setSelectedTestimonial(item);
    setIsOpenEditModal(true);
  };

  const handleOpenDeleteModal = (id) => {
    setTestimonialId(id);
    setOpenDeleteModal(true);
  };

  const handleModalClose = () => {
    setOpenAddModal(false);
    setIsOpenEditModal(false);
    setOpenDeleteModal(false);
    setTestimonialId(null);
    setSelectedTestimonial(null);
    data.refetch(); // Refresh list on close
  };

  // Delete Success Handler
  const handleDeleteSuccess = () => {
    handleModalClose();
    data.refetch();
  };

  return {
    data,
    paramsObject,
    testimonialId,
    selectedTestimonial,
    openAddModal,
    isOpenEditModal,
    openDeleteModal,
    testimonialStatus,
    isToggleLoading,
    handleTestimonialStatusChange,
    header,
    handleHeaderChange,
    handleSaveHeader,
    isHeaderUpdating,
    isHeaderChanged,
    handleSearchVal,
    handlePageClick,
    handleOpenAddModal,
    handleOpenEditModal,
    handleOpenDeleteModal,
    handleModalClose,
    handleDeleteSuccess,
  };
};

export default useTestimonialList;
